/*
 * podcast-feed.js — Client-side episode renderer for /pages/podcasts-our-changing-world
 * (Chunk H of PLAN_DUAL_HERO_AND_PODCAST).
 *
 * Wires together:
 *   - main-podcast.liquid section markup (data-* hooks: data-podcast-episodes,
 *     data-worker-base, data-podcast-slug, data-episodes-per-page, data-podcast-title,
 *     data-podcast-description, data-podcast-cover)
 *   - GET {workerBase}/podcast-feed?slug={podcastSlug}  (Chunk A Worker route)
 *   - sessionStorage cache (key: podcast-feed:{slug}:v1, max age 15 min — matches
 *     Worker's Cache-Control max-age)
 *
 * Key behaviors:
 *   - Skeleton-to-card crossfade (~600ms): the static <li.podcast-episode--skeleton>
 *     placeholders fade out via the .podcast-episode--leaving class; on transitionend
 *     they are removed from the DOM, revealing the freshly-built real episode list.
 *   - Click-to-expand: each episode <li> defaults to a 2-line description clamp
 *     (CSS: -webkit-line-clamp: 2). Clicking the row toggles data-expanded="true"
 *     on the <li>; companion CSS rule unclamps the description.
 *   - Lazy audio src: the <audio> element ships with no src attribute. On the first
 *     play-button click, audio.src = item.audioUrl is set, the [hidden] attribute is
 *     removed, audio.play() is invoked.
 *   - HTTPS verification: every URL coming from the feed (channel.image, item.image,
 *     item.audioUrl) is verified to start with "https://" before being assigned to
 *     <img src> or <audio src>. Anything else is dropped and a graceful fallback
 *     applies (skeleton image stays; play button hidden).
 *
 * Design constraints (from Chunk H plan MUST NOT):
 *   - HTML-string assignment is NEVER used for any feed-supplied field (textContent +
 *     document.createElement / setAttribute for everything that comes from the Worker).
 *   - No regex-strip of script tags — sanitization is the Worker's responsibility.
 *   - No external imports, no eval, no Function constructor.
 *   - Cache window strictly <= 15 min; older entries are ignored and refetched.
 *   - Render <= episodes_per_page items (no pagination, episodes past the cap fall off
 *     the bottom).
 *
 * Analytics: this file does NOT emit analytics events directly. Subscribe-icon
 * tracking is owned by the podcast-platform-icons snippet (Chunk F). Episode play
 * is a future-work concern (recorded in plan as deferred).
 */
(function () {
  'use strict';

  // Idempotent install guard — survives accidental double-include.
  if (window.__podcastFeedLoaded) return;
  window.__podcastFeedLoaded = true;

  var CACHE_KEY_PREFIX = 'podcast-feed:';
  var CACHE_KEY_VERSION = ':v1';
  var CACHE_MAX_AGE_MS = 15 * 60 * 1000; // 15 min — matches Worker max-age

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function isHttpsUrl(value) {
    if (typeof value !== 'string') return false;
    if (value.length < 9) return false;
    return value.slice(0, 8) === 'https://';
  }

  function cacheKeyFor(slug) {
    return CACHE_KEY_PREFIX + slug + CACHE_KEY_VERSION;
  }

  function readCache(slug) {
    try {
      var raw = window.sessionStorage.getItem(cacheKeyFor(slug));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (typeof parsed.cachedAt !== 'number') return null;
      var age = Date.now() - parsed.cachedAt;
      if (age < 0 || age > CACHE_MAX_AGE_MS) return null;
      if (!parsed.data || typeof parsed.data !== 'object') return null;
      return parsed.data;
    } catch (e) {
      // sessionStorage may be disabled / quota-exceeded in private browsing.
      return null;
    }
  }

  function writeCache(slug, data) {
    try {
      window.sessionStorage.setItem(
        cacheKeyFor(slug),
        JSON.stringify({ data: data, cachedAt: Date.now() })
      );
    } catch (e) {
      // Swallow — caching is a nicety, not a requirement.
    }
  }

  function formatDate(rfc822) {
    if (typeof rfc822 !== 'string' || !rfc822) return '';
    var d = new Date(rfc822);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return d.toDateString();
    }
  }

  function formatDuration(value) {
    // Worker emits "HH:MM:SS" or "MM:SS" strings; tolerate plain seconds too.
    if (typeof value === 'string' && /^\d{1,2}(:\d{2}){1,2}$/.test(value)) {
      // Strip a leading "00:" if present so MM:SS displays cleanly.
      if (/^00:/.test(value) && value.split(':').length === 3) {
        return value.slice(3);
      }
      return value;
    }
    var seconds = Number(value);
    if (!isFinite(seconds) || seconds <= 0) return '';
    var hh = Math.floor(seconds / 3600);
    var mm = Math.floor((seconds % 3600) / 60);
    var ss = Math.floor(seconds % 60);
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    if (hh > 0) return hh + ':' + pad(mm) + ':' + pad(ss);
    return mm + ':' + pad(ss);
  }

  function setText(el, text) {
    if (!el) return;
    el.textContent = text == null ? '' : String(text);
  }

  // ---------------------------------------------------------------------------
  // Episode <li> construction (DOM-only — never HTML-string assignment)
  // ---------------------------------------------------------------------------

  function buildEpisodeNode(item, episodeNumber) {
    var li = document.createElement('li');
    li.className = 'podcast-episode';
    if (item.guid) li.setAttribute('data-episode-guid', item.guid);
    li.setAttribute('data-expanded', 'false');

    // Top metadata line: EPISODE N · DATE · DURATION
    var meta = document.createElement('p');
    meta.className = 'podcast-episode__meta';
    var metaParts = [];
    if (typeof episodeNumber === 'number' && episodeNumber > 0) {
      metaParts.push('Episode ' + episodeNumber);
    }
    var formattedDate = formatDate(item.pubDate);
    if (formattedDate) metaParts.push(formattedDate);
    var formattedDuration = formatDuration(item.duration);
    if (formattedDuration) metaParts.push(formattedDuration);
    setText(meta, metaParts.join(' · '));
    li.appendChild(meta);

    // Title (h3)
    var title = document.createElement('h3');
    title.className = 'podcast-episode__title';
    setText(title, item.title || 'Untitled episode');
    li.appendChild(title);

    // Description (plain text, server-stripped — see Chunk A)
    var desc = document.createElement('div');
    desc.className = 'podcast-episode__description';
    setText(desc, item.description || '');
    li.appendChild(desc);

    // Action row
    var actions = document.createElement('div');
    actions.className = 'podcast-episode__actions';

    var hasAudio = isHttpsUrl(item.audioUrl);

    // Play button (only if audio URL is HTTPS)
    if (hasAudio) {
      var playBtn = document.createElement('button');
      playBtn.type = 'button';
      playBtn.className = 'podcast-episode__play';
      playBtn.setAttribute('data-audio-url', item.audioUrl);
      setText(playBtn, '▶ Play');
      actions.appendChild(playBtn);
    }

    // Open-in-app link (RSS <link> field — typically the show-notes URL)
    if (typeof item.link === 'string' && (isHttpsUrl(item.link) || item.link.charAt(0) === '/')) {
      var openLink = document.createElement('a');
      openLink.className = 'podcast-episode__open-link';
      openLink.setAttribute('href', item.link);
      openLink.setAttribute('target', '_blank');
      openLink.setAttribute('rel', 'noopener noreferrer');
      setText(openLink, 'Open in app');
      actions.appendChild(openLink);
    }

    li.appendChild(actions);

    // Inline <audio> player (lazy src; revealed on play click)
    if (hasAudio) {
      var audio = document.createElement('audio');
      audio.className = 'podcast-episode__player';
      audio.setAttribute('controls', '');
      audio.setAttribute('preload', 'none');
      audio.setAttribute('hidden', '');
      li.appendChild(audio);
    }

    return li;
  }

  // ---------------------------------------------------------------------------
  // Click handling — expand-on-row-click, lazy-audio play button
  // ---------------------------------------------------------------------------

  function attachListClickHandlers(listEl) {
    listEl.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      // Play-button click: lazily set src, reveal player, play.
      var playBtn = target.closest('.podcast-episode__play');
      if (playBtn && listEl.contains(playBtn)) {
        event.stopPropagation();
        var li = playBtn.closest('.podcast-episode');
        if (!li) return;
        var audio = li.querySelector('.podcast-episode__player');
        var audioUrl = playBtn.getAttribute('data-audio-url');
        if (!audio || !isHttpsUrl(audioUrl)) return;
        if (audio.hidden) {
          if (!audio.getAttribute('src')) {
            audio.setAttribute('src', audioUrl);
          }
          audio.hidden = false;
          setText(playBtn, '■ Hide player');
          try { audio.play(); } catch (e) { /* user-gesture issue — ignore */ }
        } else {
          audio.hidden = true;
          try { audio.pause(); } catch (e) { /* ignore */ }
          setText(playBtn, '▶ Play');
        }
        return;
      }

      // Anchor click anywhere in the row: pass through to default navigation.
      if (target.closest('a')) return;

      // Row click → toggle expanded state for the description clamp.
      var row = target.closest('.podcast-episode');
      if (!row || !listEl.contains(row)) return;
      if (row.classList.contains('podcast-episode--skeleton')) return;
      var current = row.getAttribute('data-expanded') === 'true';
      row.setAttribute('data-expanded', current ? 'false' : 'true');
    });
  }

  // ---------------------------------------------------------------------------
  // Skeleton-to-card crossfade
  // ---------------------------------------------------------------------------

  function crossfadeReplaceList(listEl, newNodes) {
    // Hide the static "Loading episodes…" liveregion, if present.
    var loading = listEl.querySelector('.podcast-episodes__loading');
    if (loading) loading.remove();

    var skeletons = listEl.querySelectorAll('.podcast-episode--skeleton');
    if (skeletons.length === 0) {
      // No skeletons — append immediately.
      newNodes.forEach(function (node) { listEl.appendChild(node); });
      return;
    }

    // Mark skeletons as leaving — CSS transitions opacity to 0 over ~300ms.
    skeletons.forEach(function (sk) { sk.classList.add('podcast-episode--leaving'); });

    var settled = false;
    function finalize() {
      if (settled) return;
      settled = true;
      skeletons.forEach(function (sk) { if (sk.parentNode) sk.parentNode.removeChild(sk); });
      newNodes.forEach(function (node) { listEl.appendChild(node); });
    }

    // Use the first skeleton's transitionend as the cue, with a safety timeout.
    var first = skeletons[0];
    if (first) {
      first.addEventListener('transitionend', finalize, { once: true });
    }
    // Safety: even if transitionend never fires (e.g. prefers-reduced-motion zeroes
    // the transition), settle after 600ms so the user always sees real episodes.
    window.setTimeout(finalize, 600);
  }

  function renderError(listEl, message) {
    var loading = listEl.querySelector('.podcast-episodes__loading');
    if (loading) loading.remove();
    var skeletons = listEl.querySelectorAll('.podcast-episode--skeleton');
    skeletons.forEach(function (sk) { if (sk.parentNode) sk.parentNode.removeChild(sk); });

    var errLi = document.createElement('li');
    errLi.className = 'podcast-episodes__error';
    setText(errLi, message);
    listEl.appendChild(errLi);
  }

  // ---------------------------------------------------------------------------
  // Channel header hydration
  // ---------------------------------------------------------------------------

  function hydrateChannel(rootScope, channel) {
    if (!channel || typeof channel !== 'object') return;

    var titleEl = rootScope.querySelector('[data-podcast-title]');
    if (titleEl && typeof channel.title === 'string' && channel.title.trim()) {
      setText(titleEl, channel.title);
    }

    var descEl = rootScope.querySelector('[data-podcast-description]');
    if (descEl) {
      // textContent ONLY — never HTML-string assignment for feed fields.
      setText(descEl, typeof channel.description === 'string' ? channel.description : '');
    }

    var coverEl = rootScope.querySelector('[data-podcast-cover]');
    if (coverEl && isHttpsUrl(channel.image)) {
      // Replace the static skeleton fallback only when a valid HTTPS URL is provided.
      coverEl.setAttribute('src', channel.image);
    }
  }

  // ---------------------------------------------------------------------------
  // Per-list initializer
  // ---------------------------------------------------------------------------

  function initList(listEl) {
    var slug = listEl.getAttribute('data-podcast-slug') || '';
    var workerBase = listEl.getAttribute('data-worker-base') || '';
    var perPageRaw = listEl.getAttribute('data-episodes-per-page');
    var perPage = parseInt(perPageRaw, 10);
    if (!isFinite(perPage) || perPage <= 0) perPage = 20;

    if (!slug || !workerBase) {
      console.error('[podcast-feed] missing data-podcast-slug or data-worker-base');
      renderError(listEl, 'Could not load episodes — visit Apple Podcasts or Spotify directly.');
      return;
    }

    // Find the closest scope for channel hydration — fall back to document.
    var scope = listEl.closest('.podcast-page') || document;

    function applyData(data) {
      if (!data || typeof data !== 'object') {
        renderError(listEl, 'Could not load episodes — visit Apple Podcasts or Spotify directly.');
        return;
      }
      hydrateChannel(scope, data.channel);

      var items = Array.isArray(data.items) ? data.items.slice(0, perPage) : [];
      if (items.length === 0) {
        renderError(listEl, 'No episodes available yet — visit Apple Podcasts or Spotify.');
        return;
      }

      // Episode numbering: items[0] is the newest. Latest gets the highest number.
      var totalItems = Array.isArray(data.items) ? data.items.length : items.length;
      var nodes = items.map(function (item, idx) {
        var episodeNumber = totalItems - idx;
        return buildEpisodeNode(item, episodeNumber);
      });

      crossfadeReplaceList(listEl, nodes);
    }

    // 1. sessionStorage cache check (cachedAt < 15 min old).
    var cached = readCache(slug);
    if (cached) {
      applyData(cached);
      return;
    }

    // 2. Network fetch.
    var url = workerBase.replace(/\/+$/, '') + '/podcast-feed?slug=' + encodeURIComponent(slug);
    fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('podcast-feed HTTP ' + res.status);
        }
        return res.json();
      })
      .then(function (data) {
        writeCache(slug, data);
        applyData(data);
      })
      .catch(function (err) {
        console.error('[podcast-feed] fetch failed:', err);
        renderError(listEl, 'Could not load episodes — visit Apple Podcasts or Spotify directly.');
      });

    // 3. Wire row-click + play-button handlers once.
    attachListClickHandlers(listEl);
  }

  // ---------------------------------------------------------------------------
  // DOM-ready bootstrap
  // ---------------------------------------------------------------------------

  function bootstrap() {
    var lists = document.querySelectorAll('[data-podcast-episodes]');
    if (lists.length === 0) return;
    lists.forEach(function (listEl) { initList(listEl); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }

})();
